import type { App, Directive } from 'vue'

const prefersReducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
const finePointer = () => typeof window !== 'undefined' && window.matchMedia?.('(hover: hover) and (pointer: fine)').matches

/* ---------------------------------------------------------------- v-reveal
 * Fades/slides an element in the first time it scrolls into view.
 * Usage: v-reveal, v-reveal="120" (delay ms), v-reveal:left, v-reveal:scale
 */
let revealObserver: IntersectionObserver | null = null
function getRevealObserver() {
  revealObserver ??= new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue
      entry.target.classList.add('is-revealed')
      revealObserver?.unobserve(entry.target)
    }
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 })
  return revealObserver
}

export const reveal: Directive<HTMLElement, number | undefined> = {
  mounted(el, binding) {
    if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') return
    el.classList.add('reveal', `reveal-${binding.arg ?? 'up'}`)
    if (binding.value) el.style.setProperty('--reveal-delay', `${binding.value}ms`)
    getRevealObserver().observe(el)
  },
  unmounted(el) { revealObserver?.unobserve(el) },
}

/* ----------------------------------------------------------------- v-tilt
 * 3D tilt + cursor spotlight. Exposes --mx/--my (px) and --rx/--ry (deg) to CSS.
 * Usage: v-tilt (default 7deg), v-tilt="4"
 */
type TiltState = { frame: number; move: (event: PointerEvent) => void; leave: () => void }
const tiltState = new WeakMap<HTMLElement, TiltState>()

export const tilt: Directive<HTMLElement, number | undefined> = {
  mounted(el, binding) {
    el.classList.add('tilt')
    if (prefersReducedMotion() || !finePointer()) return
    const max = binding.value ?? 7
    const state: TiltState = {
      frame: 0,
      move(event) {
        cancelAnimationFrame(state.frame)
        state.frame = requestAnimationFrame(() => {
          const rect = el.getBoundingClientRect()
          const x = event.clientX - rect.left
          const y = event.clientY - rect.top
          el.style.setProperty('--mx', `${x}px`)
          el.style.setProperty('--my', `${y}px`)
          el.style.setProperty('--ry', `${((x / rect.width) - 0.5) * max * 2}deg`)
          el.style.setProperty('--rx', `${((y / rect.height) - 0.5) * -max * 2}deg`)
          el.classList.add('is-tilting')
        })
      },
      leave() {
        cancelAnimationFrame(state.frame)
        el.classList.remove('is-tilting')
        el.style.setProperty('--rx', '0deg')
        el.style.setProperty('--ry', '0deg')
      },
    }
    el.addEventListener('pointermove', state.move)
    el.addEventListener('pointerleave', state.leave)
    tiltState.set(el, state)
  },
  unmounted(el) {
    const state = tiltState.get(el)
    if (!state) return
    cancelAnimationFrame(state.frame)
    el.removeEventListener('pointermove', state.move)
    el.removeEventListener('pointerleave', state.leave)
  },
}

/* -------------------------------------------------------------- v-magnetic
 * Pulls an element a few px toward the cursor while hovered.
 */
const magneticState = new WeakMap<HTMLElement, { move: (event: PointerEvent) => void; leave: () => void }>()
export const magnetic: Directive<HTMLElement, number | undefined> = {
  mounted(el, binding) {
    if (prefersReducedMotion() || !finePointer()) return
    const strength = binding.value ?? 0.22
    const move = (event: PointerEvent) => {
      const rect = el.getBoundingClientRect()
      const dx = (event.clientX - rect.left - rect.width / 2) * strength
      const dy = (event.clientY - rect.top - rect.height / 2) * strength
      el.style.transform = `translate3d(${dx}px, ${dy}px, 0)`
    }
    const leave = () => { el.style.transform = '' }
    el.style.transition = 'transform .35s cubic-bezier(.2,.8,.2,1)'
    el.addEventListener('pointermove', move)
    el.addEventListener('pointerleave', leave)
    magneticState.set(el, { move, leave })
  },
  unmounted(el) {
    const state = magneticState.get(el)
    if (!state) return
    el.removeEventListener('pointermove', state.move)
    el.removeEventListener('pointerleave', state.leave)
  },
}

export function installMotion(app: App) {
  app.directive('reveal', reveal)
  app.directive('tilt', tilt)
  app.directive('magnetic', magnetic)
}

declare module 'vue' {
  export interface GlobalDirectives {
    vReveal: typeof reveal
    vTilt: typeof tilt
    vMagnetic: typeof magnetic
  }
}
